<template>
  <div class="mb-3">
    <div class="row mb-2">
      <div class="col-md-6">
        <label class="form-label">
          {{ lang.get("admin.cdr.searchDestinationPatient") }}
        </label>
        <div class="input-group">
          <input
            v-model="searchSsn"
            type="text"
            class="form-control"
            :placeholder="lang.get('admin.cdr.lookupSsn')"
            @keydown.enter.prevent="search"
          />
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="!searchSsn.trim() || searching"
            @click="search"
          >
            <span v-if="searching" class="fas fa-spinner fa-spin" />
            {{ lang.get("admin.cdr.lookupSearch") }}
          </button>
        </div>
      </div>
    </div>

    <div v-if="searchError" class="alert alert-danger mb-2">
      {{ searchError }}
    </div>

    <div
      v-if="lookupResult?.is_registed_patient"
      class="alert alert-success mb-2"
    >
      {{ lookupResult.firstname }} {{ lookupResult.lastname }} ({{
        lookupResult.ssn
      }})
    </div>

    <div v-if="lookupResult && !lookupResult.is_registed_patient" class="mb-2">
      <div class="alert alert-info mb-2">
        {{ lang.get("admin.cdr.patientFoundExternal") }}
      </div>
      <div class="card p-3">
        <p class="mb-1">
          <strong
            >{{ lookupResult.firstname }} {{ lookupResult.lastname }}</strong
          >
        </p>
        <p class="mb-1">{{ lookupResult.ssn }}</p>
        <p v-if="lookupResult.date_of_birth" class="mb-0">
          {{ lookupResult.date_of_birth }} {{ lookupResult.gender }}
        </p>
      </div>
    </div>

    <div v-if="showManualForm">
      <div class="alert alert-warning mb-2">
        {{ lang.get("admin.cdr.noPatientFound") }}
      </div>

      <div class="row g-2 mb-2">
        <div class="col-md-6">
          <label class="form-label">{{
            lang.get("admin.cdr.lookupSsn")
          }}</label>
          <input v-model="manualForm.ssn" type="text" class="form-control" />
        </div>
        <div class="col-md-6">
          <label class="form-label">
            {{ lang.get("admin.cdr.destinationPatientGender") }}
          </label>
          <select v-model="manualForm.gender" class="form-control">
            <option value="">{{ lang.get("general.pleaseSelect") }}</option>
            <option
              v-for="option in genderOptions"
              :key="option.value"
              :value="option.value"
            >
              {{ option.label }}
            </option>
          </select>
        </div>
      </div>
      <div class="row g-2 mb-2">
        <div class="col-md-6">
          <label class="form-label">
            {{ lang.get("admin.cdr.destinationPatientFirstname") }}
          </label>
          <input
            v-model="manualForm.firstname"
            type="text"
            class="form-control"
          />
        </div>
        <div class="col-md-6">
          <label class="form-label">
            {{ lang.get("admin.cdr.destinationPatientLastname") }}
          </label>
          <input
            v-model="manualForm.lastname"
            type="text"
            class="form-control"
          />
        </div>
      </div>
      <div class="row g-2 mb-2">
        <div class="col-md-6">
          <label class="form-label">
            {{ lang.get("admin.cdr.destinationPatientDob") }}
          </label>
          <app-datepicker
            :date="manualForm.date_of_birth"
            @on-date-change="manualForm.date_of_birth = $event"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed, watch } from "vue";
import { practitionerHttpService } from "../../app/practitioner-portal/store/practitioner-http-service";
import { lang } from "../../i18n";
import AppDatepicker from "../Utils/Datepicker.vue";
import { isSwedishSsnType, normaliseSwedishSsn } from "./swedish-ssn";

interface LookupResult {
  id?: number;
  firstname: string;
  lastname: string;
  ssn: string;
  ssn_type?: string;
  date_of_birth?: string;
  gender?: string;
  is_registed_patient?: boolean;
}

interface SelectedPatient {
  id: number | null;
  firstname: string;
  lastname: string;
  ssn: string;
  ssn_type: string;
  date_of_birth: string;
  gender: string;
}

interface GenderOption {
  value: string;
  label: string;
}

const props = defineProps<{
  ssnType: string;
  centerId: number;
  genderOptions: GenderOption[];
}>();

const emit = defineEmits<{
  (e: "patient", patient: SelectedPatient | null): void;
}>();

const LOOKUP_URL = "/api/internal/admin/cdr/lookup-patient";

const searchSsn = ref("");
const searching = ref(false);
const searchError = ref<string | null>(null);
const lookupResult = ref<LookupResult | null>(null);
const showManualForm = ref(false);
const manualForm = reactive({
  ssn: "",
  firstname: "",
  lastname: "",
  date_of_birth: "",
  gender: "",
});

const isManualValid = computed(
  () =>
    !!manualForm.ssn.trim() &&
    !!manualForm.firstname.trim() &&
    !!manualForm.lastname.trim() &&
    !!manualForm.date_of_birth &&
    !!manualForm.gender,
);

const candidate = computed<SelectedPatient | null>(() => {
  if (lookupResult.value) {
    const p = lookupResult.value;
    return {
      id: p.is_registed_patient && p.id ? p.id : null,
      firstname: p.firstname,
      lastname: p.lastname,
      ssn: p.ssn,
      ssn_type: p.ssn_type ?? props.ssnType,
      date_of_birth: p.date_of_birth ?? "",
      gender: p.gender ?? "unknown",
    };
  }
  if (showManualForm.value && isManualValid.value) {
    return {
      id: null,
      ssn: manualForm.ssn,
      ssn_type: props.ssnType,
      firstname: manualForm.firstname,
      lastname: manualForm.lastname,
      date_of_birth: manualForm.date_of_birth,
      gender: manualForm.gender,
    };
  }
  return null;
});

watch(candidate, (val) => emit("patient", val));

async function search() {
  if (!searchSsn.value.trim()) return;
  searching.value = true;
  searchError.value = null;
  lookupResult.value = null;
  showManualForm.value = false;

  // Opportunistic normalisation: send the canonical 12-digit form when input parses as
  // a Swedish personnummer; otherwise send the raw text so the admin can still proceed
  // (manually create a patient with a non-standard SSN, search reservnummer, etc.).
  const trimmed = searchSsn.value.trim();
  const ssnForRequest = isSwedishSsnType(props.ssnType)
    ? normaliseSwedishSsn(trimmed) ?? trimmed
    : trimmed;

  try {
    const res = await practitionerHttpService.post(LOOKUP_URL, {
      ssn: ssnForRequest,
      ssn_type: props.ssnType,
      center_id: props.centerId,
    });
    lookupResult.value = res.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      manualForm.ssn = ssnForRequest;
      showManualForm.value = true;
    } else {
      searchError.value = lang.get("admin.cdr.submitError");
    }
  } finally {
    searching.value = false;
  }
}
</script>
